import gsap from 'gsap';
import {
  Point,
  Ticker,
  Sprite,
  Loader,
} from 'pixi.js';
import { random } from 'lodash';
import eventHub from '@/scripts/eventHub';
import state from '@/game/state';
import audio from '@/scripts/audio';
import Actor from './Actor';
import vars from '../vars';

class Flower extends Actor {
  constructor(parent, x, y = 0, id, textures) {
    super(parent);
    this.id = id;
    this.textures = textures;
    this.animatingIn = false;
    this.muted = false;
    this.direction = random(0, 1, true) > 0.5 ? 1 : -1;
    switch (state.flowerShif) {
      case 'none':
        this.xSpeed = 0;
        break;
      case 'same':
        this.xSpeed = 0.6;
        break;
      case 'variable':
        this.xSpeed = parseFloat(random(0, 1, true).toFixed(2));
        break;
      default:
        this.xSpeed = 0.6;
        break;
    }

    this.currentTextureIndex = 0;
    this.ref = new global.PIXI.projection.Container2d();
    this.ref.alpha = 0;
    this.ref.scale.y = 0;
    this.sprite = new global.PIXI.projection.Sprite2d();
    this.flower = new Sprite(textures[this.currentTextureIndex]);
    this.shadow = new Sprite(Loader.shared.resources['images/FLOWERS'].spritesheet.textures['flower-shadow.png']);
    this.sprite.proj.affine = global.PIXI.projection.AFFINE.AXIS_X;
    this.sprite.addChild(this.shadow);
    this.sprite.addChild(this.flower);
    this.ref.addChild(this.sprite);

    this.x = x - (vars.layout.STAGE_WIDTH / 2);
    this.y = y - (vars.layout.STAGE_HEIGHT / 2);
    this.catched = false;
    this.catching = false;
    this.catchPercent = 0;
    this.floatTimeline = gsap.timeline();
    this.floatTimeline
      .addLabel('start')
      .to(this.flower.position, {
        y: `-=${20}`,
        yoyo: true,
        repeat: -1,
        duration: 2,
        paused: false,
        ease: 'power2.inOut',
      }, 'start')
      .to(this.shadow.scale, {
        x: `-=${0.3}`,
        y: `-=${0.1}`,
        yoyo: true,
        repeat: -1,
        duration: 2,
        paused: false,
        ease: 'power2.inOut',
      }, 'start');

    this.frameHandle = () => { this.frame(); };

    this.resize();
    this.parent.addChild(this.ref);
  }

  resize() {
    this.sprite.anchor = new Point(0.5, 0.5);
    this.sprite.position.x = this.x;
    this.sprite.position.y = this.y;

    // this.sprite.scale.x = 1.1;
    // this.sprite.scale.y = 1.1;

    if (!this.isPowerUp && random(0, 1, true) > 0.5) {
      this.sprite.scale.x *= -1;
    }

    this.flower.anchor = new Point(0.5, 0.5);
    this.flower.width = vars.layout.FLOWER_WIDTH;
    this.flower.height = vars.layout.FLOWER_HEIGHT;

    this.shadow.anchor = new Point(0.5, 0.5);
    this.shadow.width = vars.layout.FLOWER_WIDTH;
    this.shadow.height = vars.layout.FLOWER_HEIGHT;
  }

  start() {
    Ticker.shared.add(this.frameHandle);
  }

  stop() {
    Ticker.shared.remove(this.frameHandle);
  }

  frame() {
    if (this.catchPercent >= 1) {
      this.catch();
    }

    if (!this.catched) {
      this.sprite.position.y += state.speed;
      this.sprite.position.x += this.xSpeed * this.direction;
      if (this.sprite.position.x > 100) {
        this.direction = -1;
      }
      if (this.sprite.position.x < -100) {
        this.direction = 1;
      }

      if (!this.animatingIn && this.sprite.position.y > -500) {
        this.animatingIn = true;
        gsap.to(this.ref, { alpha: 1, ease: 'power2.inOut', duration: 0.6 });
        gsap.to(this.ref.scale, { y: 1, ease: 'elastic.out(0.5, 0.3)', duration: 0.6 });
      }

      if (this.catching) {
        if (this.textures.length > 1) {
          this.currentTextureIndex = parseInt(1 + ((this.textures.length - 1) * this.catchPercent), 10);
        }
        this.flower.texture = this.textures[this.currentTextureIndex];
        this.catchPercent += state.poppingSpeed;
      }
    }
  }

  catch() {
    this.catched = true;
    if (!this.muted) {
      if (this.isPowerUp) {
        audio.play('powerup-pop');
      } else if (this.isSuper) {
        audio.play('super-pop');
      } else {
        audio.play('flower-pop');
      }
    }
    this.stop();
    state.points += 1;
    if (this.sprite._texture) {
      this.sprite.destroy({ children: true, texture: false });
    }
  }

  miss() {
    this.stop();
    if (!this.isPowerUp && !this.isSuper) {
      state.hitCounter += 1;
      eventHub.$emit('shake');
    }
    this.sprite.destroy({ children: true, texture: false });
  }
}

export default Flower;
